import type { Element } from "../types";

type ClipLike = {
  elementId?: string | null;
  start: number;
  end: number;
};

export type RuntimeVideoHotSetPlan = {
  hotIds: string[];
  activeIds: string[];
  warmIds: string[];
  totalVideoCount: number;
};

type ResolveRuntimeVideoHotSetArgs = {
  elements: Element[];
  clips: ClipLike[];
  selectedIds: string[];
  playhead: number;
  isPlaying: boolean;
};

const CLIP_EPSILON_SEC = 1 / 240;
const PLAYING_BUFFER_BEHIND_SEC = 2.5;
const PLAYING_BUFFER_AHEAD_SEC = 10;
const IDLE_BUFFER_BEHIND_SEC = 5;
const IDLE_BUFFER_AHEAD_SEC = 18;
const MAX_WARM_INACTIVE_VIDEOS = 10;
const FALLBACK_BOOTSTRAP_VIDEOS = 3;
/** Max simultaneous active video decodes to prevent HW decoder saturation */
const MAX_CONCURRENT_ACTIVE_VIDEOS = 6;

function hasRenderableVideoSource(element: Element): element is Element & { kind: "video"; src: string } {
  return (
    element.kind === "video" &&
    typeof (element as { src?: unknown }).src === "string" &&
    (element as { src: string }).src.trim().length > 0 &&
    !(element as { src: string }).src.startsWith("{{")
  );
}

function isClipActive(clip: ClipLike, playhead: number): boolean {
  return playhead + CLIP_EPSILON_SEC >= clip.start && playhead <= clip.end + CLIP_EPSILON_SEC;
}

function clipDistanceToWindow(clip: ClipLike, windowStart: number, windowEnd: number, playhead: number): number {
  if (clip.end < windowStart) return windowStart - clip.end;
  if (clip.start > windowEnd) return clip.start - windowEnd;
  if (isClipActive(clip, playhead)) return -1;
  return Math.abs(((clip.start + clip.end) * 0.5) - playhead);
}

// Structural memoization: skip full recompute when inputs haven't materially changed.
// During playback, the hot set only changes when clips cross the active/warm boundary,
// which happens at clip edges, not every playhead tick.
let _lastHotSetResult: RuntimeVideoHotSetPlan | null = null;
let _lastHotSetElementsRef: Element[] | null = null;
let _lastHotSetClipsRef: ClipLike[] | null = null;
let _lastHotSetSelectedRef: string[] | null = null;
let _lastHotSetPlayhead = -1;
let _lastHotSetIsPlaying = false;

// During playback, only recompute when playhead crosses a clip boundary (±0.5s tolerance).
const HOT_SET_PLAYHEAD_RECOMPUTE_SEC = 0.5;

export function resolveRuntimeVideoHotSet(
  args: ResolveRuntimeVideoHotSetArgs
): RuntimeVideoHotSetPlan {
  const { elements, clips, selectedIds, playhead, isPlaying } = args;

  // Fast path: if elements/clips/selection arrays are the same reference
  // and playhead hasn't moved far, reuse the last result.
  if (
    _lastHotSetResult &&
    elements === _lastHotSetElementsRef &&
    clips === _lastHotSetClipsRef &&
    selectedIds === _lastHotSetSelectedRef &&
    isPlaying === _lastHotSetIsPlaying &&
    Math.abs(playhead - _lastHotSetPlayhead) < HOT_SET_PLAYHEAD_RECOMPUTE_SEC
  ) {
    return _lastHotSetResult;
  }

  const validVideoIds = new Set<string>();
  const unclippedVideoIds = new Set<string>();
  for (const element of elements) {
    if (!hasRenderableVideoSource(element)) continue;
    validVideoIds.add(element.id);
    unclippedVideoIds.add(element.id);
  }

  const activeIds = new Set<string>();
  const warmCandidateDistances = new Map<string, number>();
  const selectedVideoIds = new Set(selectedIds.filter((id) => validVideoIds.has(id)));
  const bufferBehind = isPlaying ? PLAYING_BUFFER_BEHIND_SEC : IDLE_BUFFER_BEHIND_SEC;
  const bufferAhead = isPlaying ? PLAYING_BUFFER_AHEAD_SEC : IDLE_BUFFER_AHEAD_SEC;
  const windowStart = Math.max(0, playhead - bufferBehind);
  const windowEnd = playhead + bufferAhead;

  for (const clip of clips) {
    const elementId = clip.elementId ?? null;
    if (!elementId || !validVideoIds.has(elementId)) continue;
    unclippedVideoIds.delete(elementId);

    if (isClipActive(clip, playhead)) {
      activeIds.add(elementId);
      warmCandidateDistances.set(elementId, -1);
      continue;
    }

    if (clip.end < windowStart || clip.start > windowEnd) continue;
    const distance = clipDistanceToWindow(clip, windowStart, windowEnd, playhead);
    const previous = warmCandidateDistances.get(elementId);
    if (previous == null || distance < previous) {
      warmCandidateDistances.set(elementId, distance);
    }
  }

  // Cap active video decodes to prevent HW decoder saturation.
  // When many clips overlap at the playhead, keep only the closest ones active;
  // demoted actives move to warm tier and stay dehydrated.
  let cappedActiveIds = activeIds;
  if (activeIds.size > MAX_CONCURRENT_ACTIVE_VIDEOS) {
    const sorted = Array.from(activeIds)
      .map((id) => ({ id, dist: warmCandidateDistances.get(id) ?? 0 }))
      .sort((a, b) => a.dist - b.dist)
      .slice(0, MAX_CONCURRENT_ACTIVE_VIDEOS);
    cappedActiveIds = new Set(sorted.map((s) => s.id));
    // Demoted actives keep their warm distance for priority warm loading
    for (const id of activeIds) {
      if (!cappedActiveIds.has(id)) activeIds.delete(id);
    }
  }

  const warmIds = Array.from(warmCandidateDistances.entries())
    .filter(([elementId]) => !cappedActiveIds.has(elementId))
    .sort((a, b) => a[1] - b[1])
    .slice(0, MAX_WARM_INACTIVE_VIDEOS)
    .map(([elementId]) => elementId);

  const hotIds = new Set<string>();
  for (const id of unclippedVideoIds) hotIds.add(id);
  for (const id of selectedVideoIds) hotIds.add(id);
  for (const id of cappedActiveIds) hotIds.add(id);
  for (const id of warmIds) hotIds.add(id);

  if (hotIds.size === 0 && validVideoIds.size > 0) {
    for (const id of Array.from(validVideoIds).slice(0, FALLBACK_BOOTSTRAP_VIDEOS)) {
      hotIds.add(id);
    }
  }

  const result: RuntimeVideoHotSetPlan = {
    hotIds: Array.from(hotIds),
    activeIds: Array.from(cappedActiveIds),
    warmIds,
    totalVideoCount: validVideoIds.size,
  };

  // Cache for structural memoization
  _lastHotSetResult = result;
  _lastHotSetElementsRef = elements;
  _lastHotSetClipsRef = clips;
  _lastHotSetSelectedRef = selectedIds;
  _lastHotSetPlayhead = playhead;
  _lastHotSetIsPlaying = isPlaying;

  return result;
}
